import { Box, SxProps, Theme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl, { LngLatBoundsLike, PaddingOptions } from "mapbox-gl";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

import { GaiaMapContext } from "../../context/GaiaMapContext";
import { useMapboxResize } from "../../hooks/useGaiaMapResize";
import { DEFAULT_MAPBOX_CONFIG, MAP_PADDING } from "../../mapConstants";
import { MAP_STYLE_CONFIG, MAP_STYLE_STANDARD, MAP_STYLES } from "../../mapStyles";

type GaiaMapProps = {
    bounds: GeoJSON.BBox;
    padding?: PaddingOptions;
    sx?: SxProps<Theme>;
    onMapLoaded?: (map: mapboxgl.Map) => Promise<void>;
    interactive?: boolean;
    scrollOnBoundsChange?: boolean;
};

export function GaiaMap({
    bounds,
    padding,
    children,
    sx,
    onMapLoaded,
    interactive = true,
    scrollOnBoundsChange = false,
}: React.PropsWithChildren<GaiaMapProps>) {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <We don't want to reinitialize the map when the bounds change. The zoom useEffect will take care of that>
    useEffect(() => {
        if (mapRef.current === null && mapContainerRef.current) {
            mapRef.current = new mapboxgl.Map({
                ...DEFAULT_MAPBOX_CONFIG,
                style: MAP_STYLES[MAP_STYLE_STANDARD].styleUrl,
                config: MAP_STYLE_CONFIG,
                container: mapContainerRef.current,
                bounds: bounds as LngLatBoundsLike,
                fitBoundsOptions: {
                    padding: { ...MAP_PADDING, ...padding },
                    maxZoom: 19,
                },
                interactive,
            });

            if (interactive) {
                const navigationControl = new mapboxgl.NavigationControl({
                    showCompass: true,
                    showZoom: false,
                });

                mapRef.current.addControl(navigationControl, "bottom-left");
                mapRef.current.addControl(new mapboxgl.ScaleControl(), "bottom-left");
            }
        }

        if (mapRef.current === null) return;

        const onLoad = async () => {
            if (mapRef.current === null) return;
            await onMapLoaded?.(mapRef.current);
            setIsMapLoaded(true);
        };

        mapRef.current.on("style.load", onLoad);

        return () => {
            mapRef.current?.off("style.load", onLoad).remove();
            mapRef.current = null;
        };
    }, [setIsMapLoaded]);

    useEffect(() => {
        if (scrollOnBoundsChange === false) return;
        if (mapRef.current === null) return;
        mapRef.current.fitBounds(bounds as LngLatBoundsLike, {
            padding: { ...MAP_PADDING, ...padding },
            maxZoom: 19,
        });
    }, [bounds, padding, scrollOnBoundsChange]);

    useMapboxResize(mapRef);

    return (
        <GaiaMapContext.Provider
            value={{
                mapRef,
                isMapLoaded,
            }}
        >
            <Box sx={{ position: "relative", ...sx }}>
                <Box ref={mapContainerRef} sx={{ width: 1, height: 1, position: "absolute", left: 0, right: 0 }} />
                {children}
            </Box>
        </GaiaMapContext.Provider>
    );
}
